import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AgGrid from '../common/AgGrid';
import Icon from '../common/Icon';
import { GetMetricsDataById } from '../../services/accountService';
import { GetJobsDataById } from '../../services/accountService';
import { AisScreenLoader } from '../../utils/AisScreenLoader'; // Import the loader
import SuccessMessage from '../common/SuccessMessage';

import { canViewField, isRestrictedUser } from '../../utils/PermissionUtils';


const COLUMN_PERMISSION_MAP = {
    jobName: 'jobname',
    type: 'jobtype',
    accountingStatus: 'accountingstatus',
    cost: 'cost',
    net: 'net',
    incomePerLetter: 'incomeperletter',
    costPerLetter: 'costperletter',
    netPerLetter: 'netperletter',
    roi: 'roi',
};


const RESTRICTED_ONLY_HIDDEN_FIELDS = ['nondonors', 'responses', 'action'];

const Jobs = ({
    loadingDetails,
    errorDetails,
    displayData,
    clientId,
    profileImage,
}) => {
    const navigate = useNavigate();

  
    const showCostFields = canViewField('cost');

    const [metricsData, setMetricsData] = useState({
        totalDonors: 0,
        totalIncome: "$0",
        totalMailed: "0",
        averageGift: "$0"
    });

    const [loadingMetrics, setLoadingMetrics] = useState(false);
    const [errorMetrics, setErrorMetrics] = useState(null);

    const [jobsData, setJobsData] = useState([]);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [errorJobs, setErrorJobs] = useState(null);

   
    const [filteredJobsData, setFilteredJobsData] = useState([]);
    const [displayTotals, setDisplayTotals] = useState({
        totalMailed: 0,
        totalDonors: 0,
        totalNonDonors: 0,
        totalIncome: 0,
        totalCost: 0,
        totalNet: 0,
        responseRate: 0,
        averageGift: 0,
        incomePerLetter: 0,
        costPerLetter: 0,
        netPerLetter: 0,
        roi: 0
    });

    const [showDropdownMenu, setShowDropdownMenu] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const [filteredExportRows, setFilteredExportRows] = useState(null);
    const [clientLogo, setClientLogo] = useState(null);
    const agGridRef = useRef(null);
    const gridApiRef = useRef(null);
    const dropdownRef = useRef(null);

    const formatDateToMMDDYYYY = (date) => {
        if (!date) return '';

        const d = new Date(date);
        if (isNaN(d.getTime())) return '';

        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const year = d.getFullYear();

        return `${month}/${day}/${year}`;
    };

   
    const parseFormattedValue = (formattedValue) => {
        if (!formattedValue) return 0;
        
        const numericValue = String(formattedValue).replace(/[$%,]/g, '');
        return parseFloat(numericValue) || 0;
    };

    const formatCurrencyForTotals = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '$0.00';
        const numValue = parseFloat(value) || 0;
        const absFormatted = Math.abs(numValue).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return numValue < 0 ? `-$${absFormatted}` : `$${absFormatted}`;
    };

    const formatNumberForTotals = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0';
        const numValue = parseFloat(value) || 0;
        return numValue.toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    };

  
    const formatPercentageForTotals = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '0.00%';
        const numValue = parseFloat(value) || 0;
        return `${numValue.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}%`;
    };

  
    const formatJobRoi = (job) => {
        if (job.roi === null || job.roi === undefined || isNaN(job.roi)) return '';
        return job.jobType === "TYP"
            ? `${(job.roi * 100).toFixed(2)}%`
            : `${job.roi.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })}`;
    };

   
    const calculateTotalsFromData = useCallback((data) => {
        if (!data || data.length === 0) {
            return {
                totalMailed: 0,
                totalDonors: 0,
                totalNonDonors: 0,
                totalIncome: 0,
                totalCost: 0,
                totalNet: 0,
                responseRate: 0,
                averageGift: 0,
                incomePerLetter: 0,
                costPerLetter: 0,
                netPerLetter: 0,
                roi: 0
            };
        }

        let totalMailed = 0;
        let totalDonors = 0;
        let totalNonDonors = 0;
        let totalIncome = 0;
        let totalCost = 0;
        let totalNet = 0;

        // Sum up all the base values
        data.forEach(row => {
            if (row.isTotalsRow) return;

            totalMailed += parseFormattedValue(row.mailed);
            totalDonors += parseFormattedValue(row.donors);
            totalNonDonors += parseFormattedValue(row.nondonors);
            totalIncome += parseFormattedValue(row.income);
            totalCost += parseFormattedValue(row.cost);
            totalNet += parseFormattedValue(row.net);
        });

        
        const responseRate = totalMailed > 0 ? (totalDonors / totalMailed) * 100 : 0;
        const averageGift = totalDonors > 0 ? totalIncome / totalDonors : 0;
        const incomePerLetter = totalMailed > 0 ? totalIncome / totalMailed : 0;
        const costPerLetter = totalMailed > 0 ? totalCost / totalMailed : 0;
        const netPerLetter = totalMailed > 0 ? totalNet / totalMailed : 0;
        const roi = totalCost > 0 ? (totalIncome / totalCost) : 0;

        return {
            totalMailed: Math.round(totalMailed),
            totalDonors: Math.round(totalDonors),
            totalNonDonors: Math.round(totalNonDonors),
            totalIncome: Math.round(totalIncome * 100) / 100,
            totalCost: Math.round(totalCost * 100) / 100,
            totalNet: Math.round(totalNet * 100) / 100,
            responseRate: responseRate,
            averageGift: averageGift,
            incomePerLetter: incomePerLetter,
            costPerLetter: costPerLetter,
            netPerLetter: netPerLetter,
            roi: roi
        };
    }, []);

    
    const calculateTotalsFromFilteredData = useCallback((filteredData) => {
        if (!filteredData || filteredData.length === 0) {
            return {
                totalMailed: 0,
                totalDonors: 0,
                totalNonDonors: 0,
                totalIncome: 0,
                totalCost: 0,
                totalNet: 0,
                responseRate: 0,
                averageGift: 0,
                incomePerLetter: 0,
                costPerLetter: 0,
                netPerLetter: 0,
                roi: 0
            };
        }

        let totalMailed = 0;
        let totalDonors = 0;
        let totalNonDonors = 0;
        let totalIncome = 0;
        let totalCost = 0;
        let totalNet = 0;

        filteredData.forEach(row => {
            if (row.isTotalsRow) return;

            totalMailed += parseFormattedValue(row.mailed);
            totalDonors += parseFormattedValue(row.donors);
            totalNonDonors += parseFormattedValue(row.nondonors);
            totalIncome += parseFormattedValue(row.income);
            totalCost += parseFormattedValue(row.cost);
            totalNet += parseFormattedValue(row.net);
        });

        const responseRate = totalMailed > 0 ? (totalDonors / totalMailed) * 100 : 0;
        const averageGift = totalDonors > 0 ? totalIncome / totalDonors : 0;
        const incomePerLetter = totalMailed > 0 ? totalIncome / totalMailed : 0;
        const costPerLetter = totalMailed > 0 ? totalCost / totalMailed : 0;
        const netPerLetter = totalMailed > 0 ? totalNet / totalMailed : 0;
        const roi = totalCost > 0 ? (totalIncome / totalCost) : 0;

        return {
            totalMailed: Math.round(totalMailed),
            totalDonors: Math.round(totalDonors),
            totalNonDonors: Math.round(totalNonDonors),
            totalIncome: Math.round(totalIncome * 100) / 100,
            totalCost: Math.round(totalCost * 100) / 100,
            totalNet: Math.round(totalNet * 100) / 100,
            responseRate: responseRate,
            averageGift: averageGift,
            incomePerLetter: incomePerLetter,
            costPerLetter: costPerLetter,
            netPerLetter: netPerLetter,
            roi: roi
        };
    }, []);

    const handleFilterChanged = useCallback((params) => {
        if (params && params.filteredData) {
            setFilteredJobsData(params.filteredData);
            const newTotals = calculateTotalsFromFilteredData(params.filteredData);
            setDisplayTotals(newTotals);

            const allCount = jobsData.filter(row => !row.isTotalsRow).length;
            const rows = params.filteredData.filter(row => !row.isTotalsRow);

            if (rows.length < allCount) {
                setFilteredExportRows(rows);
            } else {
                setFilteredExportRows(null);
            }
        }
    }, [calculateTotalsFromFilteredData, jobsData]);

   
    useEffect(() => {
        if (filteredJobsData.length > 0) {
            const newTotals = calculateTotalsFromFilteredData(filteredJobsData);
            setDisplayTotals(newTotals);
        } else if (jobsData.length > 0) {
            const newTotals = calculateTotalsFromData(jobsData);
            setDisplayTotals(newTotals);
        }
    }, [jobsData, filteredJobsData, calculateTotalsFromFilteredData, calculateTotalsFromData]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdownMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        let cancelled = false;

        const rasterizeToPng = (src) => new Promise((resolve) => {
            const img = new window.Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.naturalWidth;
                    canvas.height = img.naturalHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                    resolve({
                        dataUrl: canvas.toDataURL('image/png'),
                        width: img.naturalWidth,
                        height: img.naturalHeight,
                    });
                } catch (canvasErr) {
                    console.error('Error rasterizing client logo for PDF export:', canvasErr);
                    resolve(null);
                }
            };
            img.onerror = () => resolve(null);
            img.src = src;
        });
        const prepareLogo = async () => {
            if (!profileImage) {
                setClientLogo(null);
                return;
            }
            if (/^https?:\/\//i.test(profileImage)) {
                setClientLogo(null);
                return;
            }
            try {
                const result = await rasterizeToPng(profileImage);
                if (!cancelled) {
                    setClientLogo(result && result.width > 0 && result.height > 0 ? result : null);
                }
            } catch (err) {
                if (!cancelled) setClientLogo(null);
            }
        };

        prepareLogo();

        return () => {
            cancelled = true;
        };
    }, [profileImage]);

   

    const JobIdLinkRenderer = (params) => {
        if (params.data?.isTotalsRow) {
            return <span style={{ fontWeight: 'bold' }}>{params.value || ''}</span>;
        }

        const jobId = params.value;
        const jobCode = params.data?.jobId;
        // const clientName = displayData?.name;
        const isFromClientPortal = !!clientId;

        const handleClick = (e) => {
            e.preventDefault();
            navigate(`/job-details/${jobId}`, {
                state: {
                    jobId: jobId,
                    jobCode: jobCode,

                   
                    ...(isFromClientPortal && {
                        clientName: displayData?.name,
                        clientId: clientId,
                        source: 'client-dashboard',
                        isAllClients: false
                    }),

                   
                    ...(!isFromClientPortal && {
                        source: 'job-module',
                        isAllClients: false
                    })
                }
            });
        };

        return (
            <a
                href={`/job-details/${jobId}`}
                onClick={handleClick}
                style={{
                    color: '#007bff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: 'normal'
                }}
                onMouseEnter={(e) => {
                    e.target.style.color = '#0056b3';
                }}
                onMouseLeave={(e) => {
                    e.target.style.color = '#007bff';
                }}
            >
                {jobId}
            </a>
        );
    };

    const ViewPackageButtonRenderer = (params) => {
    const handleClick = () => {
        const jobId = params.data?.jobId;
        const isFromClientPortal = !!clientId;

        navigate(`/job-details/${jobId}`, {
            state: {
                jobId: jobId,
                jobCode: jobId,
                activeTab: 'files',
                fromIntegration: false, 

                ...(isFromClientPortal && {
                    clientName: displayData?.name,
                    clientId: clientId,
                    source: 'client-dashboard',
                    isAllClients: false
                }),

                ...(!isFromClientPortal && {
                    source: 'job-module',
                    isAllClients: false
                })
            }
        });
    };

    return (
        <button className="view-package-btn" onClick={handleClick}>
            View Package
        </button>
    );
};

    const TotalsValueRenderer = (params) => {
        if (params.node.rowPinned === 'bottom') {
           
            if (params.colDef?.field === 'roi') {
                const value = Number(params.value);
                return (
                    <span style={{ fontWeight: 'bold' }}>
                        {isNaN(value) ? '0.00' : value.toFixed(2)}
                    </span>
                );
            }

            return (
                <span style={{ fontWeight: 'bold' }}>
                    {params.value ?? ''}
                </span>
            );
        }

        return params.value ?? '';
    };

    
    useEffect(() => {
        const fetchMetricsData = async () => {
            if (!clientId) return;

            setLoadingMetrics(true);
            setErrorMetrics(null);

            try {
                const response = await GetMetricsDataById(clientId);

                setMetricsData({
                    totalDonors: (response.totalDonors ?? 0).toLocaleString(),
                    totalIncome: `$${(response.totalIncome ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}`,
                    totalMailed: (response.totalMailed ?? 0).toLocaleString(),
                    averageGift: `$${(response.avgGift ?? 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    })}`
                });
            } catch (error) {
                console.error('Error fetching metrics data:', error);
                setErrorMetrics(error.message || 'Failed to load metrics data');

                setMetricsData({
                    totalDonors: 0,
                    totalIncome: "$0",
                    totalMailed: "0",
                    averageGift: "$0"
                });
            } finally {
                setLoadingMetrics(false);
            }
        };

        fetchMetricsData();
    }, [clientId]);

    // Fetch jobs data when clientId changes
    useEffect(() => {
        const fetchJobsData = async () => {
            if (!clientId) return;

            setLoadingJobs(true);
            setErrorJobs(null);

            try {
                const response = await GetJobsDataById(clientId);
                const formattedAllJobsData = response.map(job => ({
                    jobId: job.jobCode || `JOB-${job.jobID}`,
                    clientId: job.clientCode ? `${job.clientCode}` : '',
                    jobName: job.jobName || "N/A",
                    type: job.jobType || "N/A",
                    accountingStatus: job.accountingStatus || job.status || '',
                    complete: job.isCompleted ? "Y" : "N",
                    description: job.description,
                    mailDate: formatDateToMMDDYYYY(job.mailDate),
                    lastMailDate: formatDateToMMDDYYYY(job.lastMailDate),
                    lastUpdatedDate: formatDateToMMDDYYYY(job.lastUpdatedDate),
                    // Numeric values: comma separated
                    mailed: formatNumberForTotals(job.totalMailed),
                    donors: formatNumberForTotals(job.totalDonors),
                    nondonors: formatNumberForTotals(job.nonDonors),
                    responses: formatPercentageForTotals(job.responseRate),
                    // Currency values: $ with comma separated
                    averageGift: formatCurrencyForTotals(job.avgGift),
                    income: formatCurrencyForTotals(job.totalIncome),
                    cost: formatCurrencyForTotals(job.totalCost),
                    net: formatCurrencyForTotals(job.net),
                    incomePerLetter: formatCurrencyForTotals(job.incomePerLetter),
                    costPerLetter: formatCurrencyForTotals(job.costPerLetter),
                    netPerLetter: formatCurrencyForTotals(job.netPerLetter),
                    roi: job.jobType === "TYP"
                        ? `${((job.roi ?? 0) * 100).toFixed(2)}%`
                        : `${(job.roi ?? 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                        })}`,
                    action: "View Package"
                }));

                setJobsData(formattedAllJobsData);
                const totals = calculateTotalsFromData(formattedAllJobsData);
                setDisplayTotals(totals);
                setFilteredExportRows(null);

            } catch (error) {
                console.error('Error fetching jobs data:', error);
                setErrorJobs(error.message || 'Failed to load jobs data');
                setJobsData([]);
                setFilteredExportRows(null);
            } finally {
                setLoadingJobs(false);
            }
        };

        fetchJobsData();
    }, [clientId, calculateTotalsFromData]);

 
    const totalsRow = useMemo(() => ({
        isTotalsRow: true,
        jobId: '',
        clientId: '',
        type: '',
        accountingStatus: '',
        complete: '',
        description: 'TOTAL',
        mailDate: '',
        lastMailDate: '',
        lastUpdatedDate: '',
        // Numeric -> comma separated
        mailed: formatNumberForTotals(displayTotals.totalMailed),
        donors: formatNumberForTotals(displayTotals.totalDonors),
        nondonors: formatNumberForTotals(displayTotals.totalNonDonors),
        responses: formatPercentageForTotals(displayTotals.responseRate),
        // Currency -> $ with comma separated
        averageGift: formatCurrencyForTotals(displayTotals.averageGift),
        income: formatCurrencyForTotals(displayTotals.totalIncome),
        cost: showCostFields ? formatCurrencyForTotals(displayTotals.totalCost) : '',
        net: showCostFields ? formatCurrencyForTotals(displayTotals.totalNet) : '',
        incomePerLetter: canViewField('incomeperletter') ? formatCurrencyForTotals(displayTotals.incomePerLetter) : '',
        costPerLetter: showCostFields ? formatCurrencyForTotals(displayTotals.costPerLetter) : '',
        netPerLetter: showCostFields ? formatCurrencyForTotals(displayTotals.netPerLetter) : '',
        roi: showCostFields ? displayTotals.roi.toFixed(2) : '',
        action: ''
    }), [displayTotals, showCostFields]);

    const rowData = useMemo(() => {
        return jobsData || [];
    }, [jobsData]);

    
    const jobListColumnDefs = [
        {
            field: 'jobId',
            headerName: 'Job ID',
            minWidth: 220,
            filter: 'agTextColumnFilter',
            cellRenderer: JobIdLinkRenderer
        },
        {
            field: 'clientId',
            headerName: 'Client ID',
            minWidth: 160,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}></span>;
                }
                return params.value;
            }
        },
        {
            field: 'jobName',
            headerName: 'Job Name',
            width: 220,
            minWidth: 220,
            maxWidth: 220,
            filter: 'agTextColumnFilter',
            valueFormatter: (params) => {
                const value = params.value || '';
                return value.length > 25 ? value.substring(0, 25) + '...' : value;
            },
            tooltipValueGetter: (params) => params.value
        },
        { field: 'type', headerName: 'Type', minWidth: 220, filter: 'agTextColumnFilter', },
        {
            field: 'accountingStatus',
            headerName: 'Accounting Status',
            minWidth: 200,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}></span>;
                }
                return params.value;
            }
        },
      
        {
            field: 'description',
            headerName: 'Description',
            minWidth: 220,
            width: 220,
            maxWidth: 220,
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}>TOTAL</span>;
                }
                const description = params.data?.description || '';
                const displayText = description.length > 25 ? description.substring(0, 25) + '...' : description;

                return (
                    <span
                        title={description.length > 25 ? description : ''}
                        style={{
                            display: 'inline-block',
                            padding: '0px 8px'
                        }}
                    >
                        {displayText}
                    </span>
                );
            }
        },
        {
            field: 'mailDate', headerName: 'Mail Date', minWidth: 220, width: 220,
              comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            maxWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}></span>;
                }
                return params.value;
            }
        },
        {
            field: 'lastMailDate',
            headerName: 'Last Mail Date',
            minWidth: 220,
            width: 220,
            maxWidth: 220,
              comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            filter: 'agTextColumnFilter',
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}></span>;
                }
                return params.value;
            }
        },
        {
            field: 'lastUpdatedDate', headerName: 'Last Update', minWidth: 220, filter: 'agTextColumnFilter',
              comparator: (dateA, dateB) => new Date(dateA) - new Date(dateB),
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return <span style={{ fontWeight: 'bold' }}></span>;
                }
                return params.value;
            }
        },
        {
            field: 'mailed', headerName: 'Total Mailed', minWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
       
        {
            field: 'nondonors', headerName: 'Non Donors', minWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
         {
            field: 'donors', headerName: 'Donations', minWidth: 180, width: 180,
            maxWidth: 180, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },

        {
            field: 'responses', headerName: 'Response Rate', minWidth: 220, width: 220,
            maxWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'averageGift', headerName: 'Average Gift', minWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'income', headerName: 'Income', minWidth: 160, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'cost', headerName: 'Cost', minWidth: 160, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'net', headerName: 'Net', minWidth: 160, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'incomePerLetter', headerName: 'Income/Letter', minWidth: 220, width: 220,
            maxWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'costPerLetter', headerName: 'Cost/Letter', minWidth: 220, width: 220,
            maxWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'netPerLetter', headerName: 'Net/Letter', minWidth: 220, width: 220,
            maxWidth: 220, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'roi', headerName: 'ROI', minWidth: 160, filter: 'agTextColumnFilter',
            cellRenderer: TotalsValueRenderer,
            cellStyle: (params) => {
                if (params.data?.isTotalsRow) {
                    return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right', paddingRight: '15px' };
                }
                return { textAlign: 'right' };
            }
        },
        {
            field: 'action', headerName: 'Action', minWidth: 200, filter: false, sortable: false,
            cellRenderer: (params) => {
                if (params.data?.isTotalsRow) {
                    return null;
                }
                return <ViewPackageButtonRenderer {...params} />;
            }
        }
    ];

   
    const visibleColumnDefs = useMemo(
        () => {
            const userIsRestricted = isRestrictedUser();
            return jobListColumnDefs.filter((col) => {
                if (userIsRestricted && RESTRICTED_ONLY_HIDDEN_FIELDS.includes(col.field)) {
                    return false;
                }
                const permName = COLUMN_PERMISSION_MAP[col.field];
                return permName ? canViewField(permName) : true; 
            });
        },
       
        []
    );

    const allExportColumns = useMemo(() => {
        return visibleColumnDefs
            .filter((col) => col.field !== 'action')
            .map((col) => ({ field: col.field, headerName: col.headerName }));
    }, [visibleColumnDefs]);

   
    const getExportColumns = useCallback(() => {
        if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
            const visible = agGridRef.current.getVisibleColumns();
            if (Array.isArray(visible)) {
                return visible.filter((c) => c.field && c.field !== 'action');
            }
        }
        return allExportColumns;
    }, [allExportColumns]);

    const getExportableRows = useCallback(() => {
        return jobsData.filter((row) => !row.isTotalsRow);
    }, [jobsData]);
    const getExportRows = useCallback(() => {
        const allRows = getExportableRows();

        if (allRows.length === 0) {
            return { rows: [], scope: 'all' };
        }

        if (filteredExportRows !== null) {
            return { rows: filteredExportRows, scope: 'filtered' };
        }

        return { rows: allRows, scope: 'all' };
    }, [getExportableRows, filteredExportRows]);

    const isGridCurrentlyEmpty = useCallback(() => {
        if (!jobsData || jobsData.length === 0) {
            return true;
        }
        if (filteredExportRows !== null) {
            return filteredExportRows.length === 0;
        }
        return false;
    }, [jobsData, filteredExportRows]);

    const buildFormattedTotalsRow = useCallback((dataRows) => {
        const totals = calculateTotalsFromFilteredData(dataRows);
        return {
            jobId: '',
            clientId: '',
            jobName: '',
            type: '',
            accountingStatus: '',
            complete: '',
            description: 'TOTAL',
            mailDate: '',
            lastMailDate: '',
            lastUpdatedDate: '',
            mailed: formatNumberForTotals(totals.totalMailed),
            donors: formatNumberForTotals(totals.totalDonors),
            nondonors: formatNumberForTotals(totals.totalNonDonors),
            responses: formatPercentageForTotals(totals.responseRate),
            averageGift: formatCurrencyForTotals(totals.averageGift),
            income: formatCurrencyForTotals(totals.totalIncome),
            cost: showCostFields ? formatCurrencyForTotals(totals.totalCost) : '',
            net: showCostFields ? formatCurrencyForTotals(totals.totalNet) : '',
            incomePerLetter: canViewField('incomeperletter') ? formatCurrencyForTotals(totals.incomePerLetter) : '',
            costPerLetter: showCostFields ? formatCurrencyForTotals(totals.costPerLetter) : '',
            netPerLetter: showCostFields ? formatCurrencyForTotals(totals.netPerLetter) : '',
            roi: showCostFields ? totals.roi.toFixed(2) : '',
            action: ''
        };
    }, [calculateTotalsFromFilteredData, showCostFields]);

    const toggleDropdownMenu = () => {
        setShowDropdownMenu(!showDropdownMenu);
    };

    const handleMessageClose = () => {
        setMessage('');
        setMessageType('success');
    };

  
    const exportToExcel = () => {
        if (isGridCurrentlyEmpty()) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        const { rows: dataRows } = getExportRows();

        if (!dataRows || dataRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

            const exportData = dataRows.map((item) => {
                const row = {};
                exportColumns.forEach(({ field, headerName }) => {
                    row[headerName] = item[field] ?? '';
                });
                return row;
            });
            const totalsRowOut = buildFormattedTotalsRow(dataRows);
            const totalsExportRow = {};
            exportColumns.forEach(({ field, headerName }) => {
                totalsExportRow[headerName] = field === 'description' ? 'TOTAL' : (totalsRowOut[field] ?? '');
            });
            if (!exportColumns.some((c) => c.field === 'description') && exportColumns.length > 0) {
                totalsExportRow[exportColumns[0].headerName] = 'TOTAL';
            }
            exportData.push(totalsExportRow);

            const ws = XLSX.utils.json_to_sheet(exportData);

            const colWidths = [];
            Object.keys(exportData[0] || {}).forEach((key, index) => {
                const maxLength = Math.max(
                    key.length,
                    ...exportData.map((row) => {
                        const value = row[key];
                        return value ? String(value).length : 0;
                    })
                );
                colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
            });
            ws['!cols'] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Jobs');

            XLSX.writeFile(
                wb,
                `jobs-list-${new Date().toISOString().split('T')[0]}.xlsx`
            );

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Export error:', error);
            setMessage('Error exporting data to Excel');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };
    const printToPDF = () => {
        if (isGridCurrentlyEmpty()) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        const { rows: dataRows, scope } = getExportRows();

        if (!dataRows || dataRows.length === 0) {
            setMessage('No data to export');
            setMessageType('error');
            setShowDropdownMenu(false);
            return;
        }

        try {
            const exportColumns = getExportColumns();
            if (exportColumns.length === 0) {
                setMessage('No data to export');
                setMessageType('error');
                setShowDropdownMenu(false);
                return;
            }

         
            const totalsRowOut = buildFormattedTotalsRow(dataRows);

            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'pt',
                format: 'a4',
            });

            const pageWidth = doc.internal.pageSize.getWidth();
            const marginX = 20;
            const cardX = marginX;
            const cardY = 20;
            const cardWidth = pageWidth - marginX * 2;

            const titleText = 'JOB LIST';
            const subtitleText = displayData?.name ? String(displayData.name) : 'Jobs';

            const currentDate = new Date();
            const reportDateStr = `${(currentDate.getMonth() + 1)
                .toString()
                .padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

            const headerBarHeight = 60;
            const statsBarHeight = 50;
            const detailsBarHeight = 22;
            const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

            const BLUE_DARK = [30, 75, 120];
            const BLUE_MID = [46, 111, 159];
            const BLUE_DEEP = [22, 58, 95];

            // ── 1) Header bar ──────────────────────────────────────────
            doc.setFillColor(...BLUE_DARK);
            doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

            const logoW = 90;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6);
            doc.setTextColor(180, 200, 220);
            doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

            // Title centred on the FULL card width
            const cardCenterX = cardX + cardWidth / 2;
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.text(titleText, cardCenterX, cardY + headerBarHeight / 2 + 2, { align: 'center' });

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(200, 215, 230);
            doc.text(subtitleText, cardCenterX, cardY + headerBarHeight / 2 + 16, { align: 'center' });
            const metaX = cardX + cardWidth - 14;
            const metaCenterY = cardY + headerBarHeight / 2;

            const drawReportFallbackText = () => {
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(13);
                doc.setTextColor(255, 255, 255);
                doc.text('Date', metaX, metaCenterY - 2, { align: 'right' });

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.setTextColor(180, 200, 220);
                doc.text(reportDateStr, metaX, metaCenterY + 10, { align: 'right' });
            };

            if (clientLogo?.dataUrl && clientLogo.width && clientLogo.height) {
                const diameter = Math.min(88, headerBarHeight - 16);
                const radius = diameter / 2;
                const circleCenterX = cardX + cardWidth - 14 - radius;
                const circleCenterY = cardY + headerBarHeight / 2;

                const aspect = clientLogo.width / clientLogo.height;
                let drawW, drawH;
                if (aspect >= 1) {
                    drawH = diameter;
                    drawW = diameter * aspect;
                } else {
                    drawW = diameter;
                    drawH = diameter / aspect;
                }
                const logoX = circleCenterX - drawW / 2;
                const logoY = circleCenterY - drawH / 2;

                try {
                    doc.saveGraphicsState();
                    doc.circle(circleCenterX, circleCenterY, radius, null);
                    doc.clip();
                    doc.discardPath();
                    doc.addImage(clientLogo.dataUrl, 'PNG', logoX, logoY, drawW, drawH);
                    doc.restoreGraphicsState();
                    doc.setDrawColor(255, 255, 255);
                    doc.setLineWidth(1.2);
                    doc.circle(circleCenterX, circleCenterY, radius, 'S');
                } catch (imgErr) {
                    console.error('Error drawing client logo in PDF:', imgErr);
                    drawReportFallbackText();
                }
            } else {
                drawReportFallbackText();
            }
            const statsY = cardY + headerBarHeight;
            doc.setFillColor(...BLUE_MID);
            doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');
            const stats = [
                { label: 'TOTAL MAILED', value: totalsRowOut.mailed },
                { label: 'TOTAL DONORS', value: totalsRowOut.donors },
                { label: 'AVG GIFT', value: totalsRowOut.averageGift },
                { label: 'TOTAL INCOME', value: totalsRowOut.income },
            ];
            if (showCostFields) {
                stats.push({ label: 'TOTAL COST', value: totalsRowOut.cost });
                stats.push({ label: 'TOTAL NET', value: totalsRowOut.net });
            }

            const statCellW = cardWidth / stats.length;
            stats.forEach((stat, i) => {
                const cellX = cardX + i * statCellW;
                if (i > 0) {
                    doc.setDrawColor(80, 130, 170);
                    doc.setLineWidth(0.5);
                    doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6);
                }
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(6.5);
                doc.setTextColor(200, 220, 235);
                doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(11);
                doc.setTextColor(255, 255, 255);
                doc.text(String(stat.value ?? ''), cellX + statCellW / 2, statsY + 35, { align: 'center' });
            });

            // ── 3) Details strip ──────────────────────────────────────
            const detailsY = statsY + statsBarHeight;
            doc.setFillColor(...BLUE_DEEP);
            doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text('JOB DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

            const scopeLabel = scope === 'filtered' ? 'Filtered' : 'All';
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(200, 215, 230);
            doc.text(
                `${scopeLabel} Records: ${dataRows.length}  ·  Confidential`,
                cardX + cardWidth - 12,
                detailsY + detailsBarHeight / 2 + 3,
                { align: 'right' }
            );

            // ── Table ────────────────────────────────────────────────
            const tableStartY = cardY + cardHeight + 10;
            const head = [exportColumns.map((c) => c.headerName)];
            const body = dataRows.map((item) =>
                exportColumns.map(({ field }) => item[field] ?? '')
            );

            const footerRow = exportColumns.map(({ field }) =>
                field === 'description' ? 'TOTAL' : (totalsRowOut[field] ?? '')
            );
            if (!exportColumns.some((c) => c.field === 'description') && exportColumns.length > 0) {
                footerRow[0] = 'TOTAL';
            }

            const rightAlignedFields = [
                'mailed', 'donors', 'nondonors', 'responses', 'averageGift',
                'income', 'cost', 'net', 'incomePerLetter', 'costPerLetter',
                'netPerLetter', 'roi',
            ];

            autoTable(doc, {
                head,
                body,
                foot: [footerRow],
                startY: tableStartY,
                margin: { left: marginX, right: marginX },
                styles: {
                    fontSize: 6.5,
                    cellPadding: 3,
                    overflow: 'linebreak',
                    valign: 'middle',
                },
                headStyles: {
                    fillColor: [46, 111, 159],
                    textColor: [255, 255, 255],
                    fontStyle: 'bold',
                    fontSize: 7,
                },
                footStyles: {
                    fillColor: [240, 248, 255],
                    textColor: [0, 0, 0],
                    fontStyle: 'bold',
                },
                alternateRowStyles: {
                    fillColor: [250, 250, 250],
                },
                didParseCell: (data) => {
                    if (data.section === 'body' || data.section === 'foot') {
                        const col = exportColumns[data.column.index];
                        if (col && rightAlignedFields.includes(col.field)) {
                            data.cell.styles.halign = 'right';
                        }
                    }
                },
                didDrawPage: () => {
                    const pageCount = doc.internal.getNumberOfPages();
                    const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
                    const pageHeight = doc.internal.pageSize.getHeight();
                    doc.setFontSize(8);
                    doc.setTextColor(120, 120, 120);
                    doc.text(
                        `Page ${pageCurrent} of ${pageCount}`,
                        pageWidth - marginX,
                        pageHeight - 10,
                        { align: 'right' }
                    );
                },
            });

            doc.save(`jobs-list-${new Date().toISOString().split('T')[0]}.pdf`);

            setMessage('Export completed successfully!');
            setMessageType('success');
            setShowDropdownMenu(false);

        } catch (error) {
            console.error('Error exporting to PDF:', error);
            setMessage('Error exporting data to PDF');
            setMessageType('error');
            setShowDropdownMenu(false);
        }
    };

   
    const handleGridReady = useCallback((params) => {
        gridApiRef.current = params.api;
    }, []);

    
    if (loadingDetails) {
        return <AisScreenLoader />;
    }

    return (
        <>
            <SuccessMessage
                message={message}
                onClose={handleMessageClose}
                type={messageType}
            />

            
            <div className="metrics-section">
                <h5 className="metrics-title">{displayData.name} Job List</h5>

                {loadingMetrics && (
                    <div className="metrics-loading p-2 mb-3 text-center">
                        <AisScreenLoader />
                    </div>
                )}

                {!loadingMetrics && !errorMetrics && (
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <div className='metric-card-text'>
                                <div className="metric-label">Total Donors</div>
                                <div className="metric-value">{metricsData.totalDonors}</div>
                            </div>
                            <Icon
                                name="total-donors"
                                size={60}
                                alt="Total donors"
                            />
                        </div>
                        <div className="metric-card">
                            <div className='metric-card-text'>
                                <div className="metric-label">Total Income</div>
                                <div className="metric-value">{metricsData.totalIncome}</div>
                            </div>
                            <Icon
                                name="total-income"
                                size={60}
                                alt="Total income"
                            />
                        </div>
                        <div className="metric-card">
                            <div className='metric-card-text'>
                                <div className="metric-label">Total Mailed</div>
                                <div className="metric-value">{metricsData.totalMailed}</div>
                            </div>
                            <Icon
                                name="total-mailed"
                                size={60}
                                alt="Total mailed"
                            />
                        </div>
                        <div className="metric-card">
                            <div className='metric-card-text'>
                                <div className="metric-label">Average Gift</div>
                                <div className="metric-value">{metricsData.averageGift}</div>
                            </div>
                            <Icon
                                name="average-gift"
                                size={60}
                                alt="Average gift"
                            />
                        </div>
                    </div>
                )}
            </div>

            {loadingJobs && (
                <div className="loading-jobs-container p-3 text-center bg-light rounded mb-3">
                    <AisScreenLoader />
                </div>
            )}

            {errorJobs && !loadingJobs && (
                <div className="alert alert-warning mb-3" role="alert">
                    <small>Jobs Data: {errorJobs}</small>
                </div>
            )}

            {!loadingJobs && !errorJobs && jobsData.length > 0 && (
                <>
                    <div className="export-button-client-dashboard" ref={dropdownRef}>
                        <button
                            className="job-details-menu-buttons"
                            onClick={toggleDropdownMenu}
                        >
                            <span className="menu-dot"></span>
                            <span className="menu-dot"></span>
                            <span className="menu-dot"></span>
                        </button>

                        {showDropdownMenu && (
                            <div className="dropdown-menu-container">
                                <button
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        exportToExcel();
                                        setShowDropdownMenu(false);
                                    }}
                                >
                                    Export to Excel
                                </button>

                                <button
                                    className="dropdown-menu-item"
                                    onClick={() => {
                                        printToPDF();
                                        setShowDropdownMenu(false);
                                    }}
                                >
                                    Print To PDF
                                </button>
                            </div>
                        )}
                    </div>

                    <AgGrid
                        ref={agGridRef}
                        rowData={rowData}
                        pinnedBottomRowData={[totalsRow]}
                        columnDefs={visibleColumnDefs}
                        hideExportButton={true}
                        hideSideBar={true}
                        hideSelectAll={true}
                        height="500px"
                        onGridReady={handleGridReady}
                        onFilterChanged={handleFilterChanged}
                        gridOptions={{
                            getRowStyle: (params) => {
                                if (params.node.rowPinned === 'bottom') {
                                    return {
                                        backgroundColor: '#f0f8ff',
                                        fontWeight: 'bold'
                                    };
                                }
                                return null;
                            },

                        }}
                    />
                </>
            )}

            {!loadingJobs && !errorJobs && jobsData.length === 0 && (
                <div className="alert alert-info mb-3" role="alert">
                    <span>No record found</span>
                </div>
            )}
        </>
    );
};

export default Jobs;